import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useExchangeRates } from '../services/exchangeRates';
import { useRelevantDocs, useSingleDoc, useFreshCountries, useFreshCities } from '../services/firebase';
import { Talent, List } from '../shared/types';
import { listPlatforms } from '../shared/platforms';
import { contentTags } from '../shared/grailConstants';
import { LIST_ORDER_SORT_BY } from '../utils/orderedTalentIds';
import { getBrowseFilterFromList } from '@/utils/browseFilter';
import { BROWSE_SKELETON_COLUMNS, TalentBrowse, TalentBrowseTable } from '@/components/talentBrowse';
import { useSkeletonPhases } from '@/hooks/useSkeletonPhases';

import Layout from '../components/Layout';
import { AppContainer } from '../components/AppContainer';
import { Button } from '@/components/ui/button';
import { Heading } from '@/components/typography';


export default function Manager() {
    const { listId } = useParams<{ listId: string }>();

    const listQuery = useSingleDoc<List>({ collectionName: 'lists', docId: listId || '', enabled: Boolean(listId) });
    const list = listQuery.data;
    const hasList = Boolean(list?.name);

    const listTalentIds = listId ? Object.keys(list?.talent || {}) : [];
    const listTalentQuery = useRelevantDocs<Talent>({ collectionName: 'talent', docIds: listTalentIds, enabled: Boolean(list) })
    const listTalent = listTalentQuery.data;

    const exchangeRates = useExchangeRates();

    const countriesQuery = useFreshCountries();
    const countryList = countriesQuery.flatData.filter((country) => country.liveOnAirtable).map((country) => country.label).sort();
    const cityQuery = useFreshCities();
    const cityList = cityQuery.flatData.map((cityDoc) => cityDoc.city).sort();
    const tagList = contentTags;

    const talentLoading = Boolean(hasList && listTalentIds.length > 0 && !listTalentQuery.isSuccess && !listTalentQuery.isError);
    const waitingForList = Boolean(listId) && !listQuery.isError && !listQuery.isSuccess;
    const phase = useSkeletonPhases(waitingForList);
    const platforms = useMemo(() => (list ? listPlatforms(list) : []), [list]);
    const currency = list?.currency || "USD";
    const campaignType = list?.campaignType || "Song";
    const lists = useMemo(() => (list ? [{ ...list, active: true }] : []), [list]);
    const defaultFilters = useMemo(
        () => (list ? getBrowseFilterFromList(list, platforms) : undefined),
        [list, platforms],
    );

    return (
        <Layout gray={true}>
            <AppContainer className="mt-12 mb-12">
                {!listId ? (
                    <div className="my-12 text-center">
                        <Heading type="h3" className="mb-2">List not found</Heading>
                        <div className="text-muted-foreground">This public list link is missing an id.</div>
                    </div>
                ) : listQuery.isError ? (
                    <div className="my-12 text-center">
                        <Heading type="h3" className="mb-2">Couldn&apos;t load this list</Heading>
                        <div className="mb-4 text-muted-foreground">Check your connection and try again.</div>
                        <Button type="button" onClick={() => void listQuery.refetch()}>
                            Retry
                        </Button>
                    </div>
                ) : !hasList && listQuery.isSuccess ? (
                    <div className="my-12 text-center">
                        <Heading type="h3" className="mb-2">List not found</Heading>
                        <div className="text-muted-foreground">
                            <p>This list may have been deleted or the link is incorrect.</p>
                            <p>If you think this is an error, please share the full URL from the URL bar.</p>
                        </div>
                    </div>
                ) : listTalentQuery.isError && listTalentIds.length > 0 && !listTalent?.length ? (
                    <div className="my-12 text-center">
                        <Heading type="h3" className="mb-2">Couldn&apos;t load creators</Heading>
                        <div className="mb-4 text-muted-foreground">The list loaded, but creator data failed to load.</div>
                        <Button type="button" onClick={() => void listTalentQuery.refetch()}>
                            Retry
                        </Button>
                    </div>
                ) : phase === "showSkeleton" ? (
                    <TalentBrowseTable columns={BROWSE_SKELETON_COLUMNS} rows={[]} empty={null} isLoading />
                ) : phase === "showResult" && list ? (
                    <TalentBrowse
                        talent={listTalent || []}
                        lists={lists}
                        platforms={platforms}
                        campaignType={campaignType}
                        currency={currency}
                        exchangeRates={exchangeRates}
                        defaultListId={list.id}
                        defaultSortBy={LIST_ORDER_SORT_BY}
                        defaultFilters={defaultFilters}
                        hidePrices={list.hidePublicPrices}
                        isLoading={talentLoading}
                        filterSheet={
                            list.hidePublicFilters
                                ? undefined
                                : {
                                    showPriceChips: true,
                                    currency,
                                    countryList,
                                    tagList,
                                    cityList,
                                }
                        }
                        empty="No creators match your filters."
                    />
                ) : phase === "showResult" ? (
                    <TalentBrowseTable columns={BROWSE_SKELETON_COLUMNS} rows={[]} empty={null} isLoading />
                ) : null}
            </AppContainer>
        </Layout>
    );
}
